import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Box, ListItem, Typography } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    successMessage: {
        fontSize: pxToRem(18),
        color: theme.palette.common.black,
    },
    skippedSection: {
        marginTop: theme.spacing(2),
    },
    skippedHeader: {
        fontSize: pxToRem(14),
        color: '#EB5757',
        fontWeight: 500,
        marginBottom: theme.spacing(1),
    },
    skippedListContainer: {
        maxHeight: pxToRem(200),
        overflowY: 'auto',
        border: '1px solid #ACB1AD',
        borderRadius: '4px',
        padding: theme.spacing(1),
        backgroundColor: '#F5F5F5',
        marginTop: theme.spacing(1),
    },
    skippedListItem: {
        fontSize: pxToRem(14),
        paddingLeft: 0,
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
        display: 'list-item',
        marginLeft: theme.spacing(2),
    },
    skippedItemText: {
        fontSize: pxToRem(14),
        color: theme.palette.common.black,
    },
    helpText: {
        fontSize: pxToRem(14),
        color: '#304959',
        marginTop: theme.spacing(1),
        display: 'block',
    },
}))

interface BulkAddSuccessDialogContentProps {
    /** Number of elements successfully added */
    addedCount: number
    /** Tree paths of elements that could not be added due to errors */
    skippedElementPaths?: string[]
}

/**
 * Formats a tree path for display by replacing separators with more readable characters.
 * Replaces "||" with " > " (hierarchy separator) and "|" with " - " (value separator).
 * @param path - The raw tree path from the backend
 * @returns Formatted path string
 */
const formatTreePath = (path: string): string => {
    // First replace || with a placeholder to avoid conflicts
    return path
        .replace(/\|\|/g, ' > ')
        .replace(/\|/g, ' - ')
}

/**
 * Content component for the bulk add success dialog.
 * Displays the count of successfully added elements and optionally shows
 * a scrollable list of elements that could not be added due to errors.
 */
const BulkAddSuccessDialogContent: React.FC<BulkAddSuccessDialogContentProps> = ({
    addedCount,
    skippedElementPaths,
}) => {
    const classes = useStyles()
    const hasSkippedElements = skippedElementPaths && skippedElementPaths.length > 0
    const skippedCount = skippedElementPaths?.length ?? 0
    const actualAddedCount = addedCount - skippedCount

    return (
        <>
            <Typography className={classes.successMessage}>
                You have added {actualAddedCount} element{actualAddedCount !== 1 ? 's' : ''} to the project.
            </Typography>
            {hasSkippedElements && (
                <Box className={classes.skippedSection}>
                    <Typography className={classes.skippedHeader}>
                        {skippedCount} element{skippedCount !== 1 ? 's' : ''} could not be added due to errors:
                    </Typography>
                    <Box className={classes.skippedListContainer}>
                        {skippedElementPaths.map((path, index) => (
                            <ListItem key={index} className={classes.skippedListItem}>
                                <Typography className={classes.skippedItemText}>
                                    {formatTreePath(path)}
                                </Typography>
                            </ListItem>
                        ))}
                    </Box>
                    <Typography className={classes.helpText}>
                        Try adding these elements individually. If issues persist, please contact support.
                    </Typography>
                </Box>
            )}
        </>
    )
}

export default BulkAddSuccessDialogContent
